'use client';

import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormattedInput } from '@/components/organism/form/FormattedInput';
import { FormattedCustomSelect } from '@/components/ui/custom-select';
import { toast } from 'sonner';
import { Form } from '@/components/atoms/form';
import { formatCnpj, formatCPF, formatPhone } from '@/lib/formatters';
import {
  hasInjectionPattern,
  hasUnsafeContent,
  isValidCnpj,
} from '@/lib/validators';
import { FormattedOptimizedSelect } from './optimized_select';
import { FormattedTextarea } from '@/components/organism/form/FormattedTextarea';
import { LoadingButton } from '@/components/ui/loading-button';
import {
  submitSupportAgencyForm,
  SupportAgencyFormData,
} from './support-agency-actions';

const ProductOptions = [
  'CAIXA Alimentação',
  'CAIXA Refeição',
  'CAIXA Multi',
  'tagCAIXA',
  'tagCAIXA Empresas',
  'frotaCAIXA',
  'vtCAIXA',
] as const;

const estado_options = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
];

const NAME_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ'’\- ]+$/;
const PHONE_REGEX = /^\([1-9][1-9]\) 9\d{4}-\d{4}$/;
const CONTENT_MESSAGE =
  'O texto contém caracteres ou termos não permitidos (ex.: < > ; -- ou palavras reservadas).';

const baseSchema = z.object({
  nome: z
    .string({
      required_error: 'Nome é obrigatório',
    })
    .trim()
    .min(3, 'Nome deve ter pelo menos 3 caracteres')
    .max(120, 'Nome deve ter no máximo 120 caracteres')
    .regex(NAME_REGEX, 'Nome deve conter apenas letras'),
  matricula: z
    .string({
      required_error: 'Matrícula é obrigatória',
    })
    .regex(/^[Cc]\d{6}$/, 'Matrícula inválida'),
  email: z
    .string({
      required_error: 'Email é obrigatório',
    })
    .min(1, 'Email é obrigatório')
    .refine((val) => /@(caixaprepagos\.com\.br|caixa\.gov\.br)$/.test(val), {
      message: 'Email deve terminar em @caixaprepagos.com.br ou @caixa.gov.br',
    }),
  emailCliente: z
    .string({
      required_error: 'Email do cliente é obrigatório',
    })
    .min(1, 'Email do cliente é obrigatório')
    .email('Email do cliente inválido'),
  celular: z
    .string({
      required_error: 'Celular é obrigatório',
    })
    .min(1, 'Celular é obrigatório')
    .regex(PHONE_REGEX, 'Celular inválido. Use o formato (XX) 9XXXX-XXXX'),
  agencia: z
    .string({
      required_error: 'Agência é obrigatória',
    })
    .trim()
    .min(1, 'Agência é obrigatória')
    .max(150, 'Agência deve ter no máximo 150 caracteres'),
  cidade: z
    .string({
      required_error: 'Cidade é obrigatória',
    })
    .trim()
    .min(2, 'Cidade deve ter pelo menos 2 caracteres')
    .max(100, 'Cidade deve ter no máximo 100 caracteres')
    .refine((v) => !hasUnsafeContent(v), { message: CONTENT_MESSAGE }),
  Estado: z
    .string({
      required_error: 'Estado é obrigatório',
    })
    .min(1, 'Estado é obrigatório'),
  produtos: z
    .array(z.enum(ProductOptions))
    .nonempty('Selecione ao menos um produto'),
  cliente: z
    .string({
      required_error: 'Cliente é obrigatório',
    })
    .trim()
    .min(2, 'Cliente deve ter pelo menos 2 caracteres')
    .max(150, 'Cliente deve ter no máximo 150 caracteres')
    .refine((v) => !hasUnsafeContent(v), { message: CONTENT_MESSAGE }),
  cnpj: z.string().optional(),
  cpf: z.string().optional(),
  message: z
    .string({
      required_error: 'Mensagem é obrigatória',
    })
    .trim()
    .min(10, 'Mensagem deve ter pelo menos 10 caracteres')
    .max(2000, 'Mensagem deve ter no máximo 2000 caracteres')
    .refine((v) => !hasInjectionPattern(v), {
      message:
        'A mensagem contém um trecho parecido com um comando não permitido.',
    }),
});

const schema = baseSchema.superRefine((data, ctx) => {
  const hasTagCaixa = data.produtos.includes('tagCAIXA');

  if (hasTagCaixa) {
    if (!data.cpf || data.cpf.trim().length < 14) {
      ctx.addIssue({
        path: ['cpf'],
        message: 'CPF é obrigatório e deve ser válido',
        code: z.ZodIssueCode.custom,
      });
    }
  } else {
    const cnpj = data.cnpj?.trim() ?? '';
    if (!cnpj || cnpj.length < 18) {
      ctx.addIssue({
        path: ['cnpj'],
        message: 'CNPJ é obrigatório e deve ser válido',
        code: z.ZodIssueCode.custom,
      });
    } else if (!isValidCnpj(cnpj)) {
      ctx.addIssue({
        path: ['cnpj'],
        message: 'CNPJ inválido',
        code: z.ZodIssueCode.custom,
      });
    }
  }
});

type FormValues = z.infer<typeof schema>;

interface Agencia {
  label: string;
}

export default function SupportAgencyForm() {
  const [agencias, setAgencias] = useState<Agencia[]>([]);

  useEffect(() => {
    fetch('/data/agencies.json')
      .then((res) => res.json())
      .then((data) => setAgencias(data))
      .catch((err) => console.error('Erro ao carregar clientes:', err));
  }, []);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      nome: '',
      matricula: '',
      email: '',
      emailCliente: '',
      celular: '',
      agencia: '',
      cidade: '',
      Estado: '',
      produtos: [],
      cliente: '',
      cnpj: '',
      cpf: '',
      message: '',
    },
  });

  const watchProdutos = form.watch('produtos');
  const isCPF = watchProdutos?.includes('tagCAIXA');

  const { isSubmitting } = form.formState;

  // Limpa o documento que não se aplica ao alternar o produto, evitando que um
  // valor antigo (ex.: CPF digitado e depois tagCAIXA desmarcada) seja enviado.
  useEffect(() => {
    if (isCPF) {
      form.resetField('cnpj');
    } else {
      form.resetField('cpf');
    }
  }, [isCPF, form]);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      const result = await submitSupportAgencyForm(
        data as SupportAgencyFormData
      );
      if (result.ok) {
        toast.success('Formulário enviado com sucesso!');
        form.reset();
      } else {
        console.error('Erro na resposta:', result);
        toast.error(result.message);
      }
    } catch (err) {
      console.error('Erro ao submeter formulário', err);
      toast.error('Erro ao enviar formulário. Tente novamente mais tarde.');
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-6 max-w-2xl mx-auto px-6 py-10 lg:px-12 bg-white rounded-2xl shadow-xl shadow-gray-200/50 mb-16 border border-gray-100"
      >
        <div className="space-y-3 mb-8 text-center">
          <h1 className="text-bluecaixa text-3xl lg:text-4xl font-extrabold tracking-tight">
            Solicite apoio de um executivo da CAIXA Pré-Pagos
          </h1>
          <p className="text-gray-500 font-medium">
            Esta é uma área exclusiva para empregados CAIXA tirarem dúvidas ou
            pedir apoio ao negócio
          </p>
          <p className="text-gray-500 font-medium text-sm">
            Envie seus dados e entraremos em contato via telefone em até 1 dia
            útil.
          </p>
          <div className="h-1 w-16 bg-orangecaixa mx-auto rounded-full mt-4 mb-6"></div>
        </div>
        <FormattedInput
          required={false}
          name="nome"
          control={form.control}
          placeholder="Nome*"
        />
        <FormattedInput
          required={false}
          name="matricula"
          control={form.control}
          formatter={(v) => v.toUpperCase()}
          placeholder="Matrícula*"
        />
        <FormattedInput
          required={false}
          name="email"
          control={form.control}
          placeholder="Email* (@caixaprepagos.com.br ou @caixa.gov.br)"
          type="email"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormattedInput
            required={false}
            name="celular"
            control={form.control}
            formatter={formatPhone}
            placeholder="Celular*"
          />

          <FormattedOptimizedSelect
            name="agencia"
            control={form.control}
            options={agencias.map((agencia) => {
              return { label: agencia.label, value: agencia.label };
            })}
            placeholder="Agência*"
            className="min-h-[50px] px-4 bg-white border border-gray-200 text-black text-sm"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormattedInput
            required={false}
            name="cidade"
            control={form.control}
            placeholder="Cidade*"
          />
          <FormattedCustomSelect
            name="Estado"
            control={form.control}
            options={estado_options}
            placeholder="Estado*"
            className="min-h-[50px] px-4 bg-white border border-gray-200 text-black text-sm"
          />
        </div>

        <div className="bg-gray-50/50 p-6 rounded-xl border border-gray-100">
          <p className="font-semibold text-gray-700 mb-4">
            Selecione os Produtos*
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ProductOptions.map((prod) => (
              <label
                key={prod}
                className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 bg-white hover:border-bluecaixa hover:shadow-sm transition-all cursor-pointer"
              >
                <input
                  type="checkbox"
                  value={prod}
                  onClick={() => {}}
                  {...form.register('produtos')}
                  className="w-4 h-4 text-bluecaixa border-gray-300 rounded focus:ring-bluecaixa"
                />
                <span className="text-sm font-medium text-gray-700">
                  {prod}
                </span>
              </label>
            ))}
          </div>
          {form.formState.errors.produtos && (
            <p className="text-destructive text-sm mt-3">
              {form.formState.errors.produtos.message}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormattedInput
            required={false}
            name="cliente"
            control={form.control}
            placeholder="Cliente*"
          />
          <FormattedInput
            required={false}
            name={isCPF ? 'cpf' : 'cnpj'}
            control={form.control}
            formatter={isCPF ? formatCPF : formatCnpj}
            maxLength={isCPF ? 14 : 18}
            placeholder={isCPF ? 'CPF*' : 'CNPJ*'}
          />
        </div>
        <FormattedInput
          required={false}
          name="emailCliente"
          control={form.control}
          placeholder="Email do Cliente*"
          type="email"
        />
        <FormattedTextarea
          required={false}
          name="message"
          control={form.control}
          placeholder="Mensagem*"
        />

        <LoadingButton
          isLoading={isSubmitting}
          className="w-full h-14 bg-[#E56A00] text-white text-xl font-bold rounded-xl hover:bg-[#CC5E00] transition-colors shadow-lg shadow-orangecaixa/20"
          type="submit"
        >
          Enviar Solicitação
        </LoadingButton>
      </form>
    </Form>
  );
}
